import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Upload, Check } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead } from "../components/Bits";
import { Chip, inputCls, Field, readFiles } from "../components/QuoteForm";
import {
  FACILITY_TYPES,
  SPORT_OPTIONS,
  PRODUCT_OPTIONS,
  TIMELINES,
} from "../lib/site";
import { IMAGES } from "../lib/images";

const OPTIONS = [
  "Facility-specific layouts",
  "Custom sizes",
  "Brand colours",
  "Printed logos",
  "Training-zone markings",
  "Competition areas",
  "Wall-pad systems",
  "Corners and columns",
  "Complete mat and wall packages",
];

const STEPS = [
  "What are you building?",
  "Which sports?",
  "Which products?",
  "Approximate dimensions",
  "Location",
  "Timeline",
  "Contact details",
];

export default function CustomSolutions() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    facility_type: "",
    sports: [],
    products: [],
    area: "",
    city: "",
    state: "",
    timeline: "",
    full_name: "",
    organisation: "",
    phone: "",
    email: "",
    message: "",
  });

  useSeo({
    title: "Custom Combat Mats and Wall Pads | MAKO Custom | MAKO Mats",
    description:
      "Custom colours, sizes, printed logos, training-zone markings and complete mat and wall-pad packages for combat facilities in India.",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const canAdvance = () => {
    if (step === 0) return !!form.facility_type;
    if (step === 1) return form.sports.length > 0;
    if (step === 2) return form.products.length > 0;
    if (step === 3) return form.area.trim().length > 0;
    if (step === 4) return form.city.trim().length > 0;
    if (step === 5) return !!form.timeline;
    return true;
  };

  const submit = async () => {
    if (
      form.full_name.trim().length < 2 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) ||
      form.phone.trim().length < 6
    ) {
      toast.error("Please complete your name, phone and a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const attachments = await readFiles(files);
      await http.post("/enquiries", { ...form, attachments, source: "custom-project" });
      setDone(true);
      toast.success("Project enquiry received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="custom-page">
      <PageHero
        eyebrow="MAKO Custom"
        title="Your space."
        subtitle="Your surface."
        copy="Custom colours, dimensions, branding and complete surface packages for new and existing facilities."
        image={IMAGES.custom_logo}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead
            eyebrow="What can be customised"
            title="Planned around your room and your brand"
            copy="Custom work is planned to confirmed dimensions and signed off on a layout drawing before production."
          />
          <div className="mt-12 grid gap-px border border-[#111111]/10 bg-[#111111]/10 sm:grid-cols-2 lg:grid-cols-3">
            {OPTIONS.map((o, i) => (
              <Reveal key={o} delay={(i % 3) * 0.06}>
                <div className="flex h-full items-start gap-4 bg-white p-7">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C81]" />
                  <p className="text-sm font-semibold text-[#111111]">{o}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        id="project"
        className="border-t border-[#111111]/10 bg-[#F5F5F2] py-16 lg:py-24"
        data-testid="project-enquiry"
      >
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          {done ? (
            <div
              data-testid="project-success"
              className="border border-[#0F4C81]/25 bg-white p-10 sm:p-16"
            >
              <p className="mako-label text-[#0F4C81]">Project received</p>
              <h2 className="mt-6 font-display text-2xl font-bold uppercase leading-tight text-[#111111] sm:text-3xl">
                Thank you. MAKO will review your space and contact you.
              </h2>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-[#3B3B3B]">
                We will look at the dimensions, disciplines and timeline you have shared and come
                back with suitable surface and wall-padding options.
              </p>
            </div>
          ) : (
            <>
              <SectionHead
                eyebrow={`Step ${step + 1} of ${STEPS.length}`}
                title={STEPS[step]}
                copy="A short, structured enquiry so we can plan properly rather than guess."
              />

              <div className="mt-8 flex gap-1.5" data-testid="project-progress">
                {STEPS.map((s, i) => (
                  <span
                    key={s}
                    className={`h-[3px] flex-1 transition-colors duration-500 ${
                      i <= step ? "bg-[#0F4C81]" : "bg-[#111111]/12"
                    }`}
                  />
                ))}
              </div>

              <div className="mt-12 min-h-[280px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {step === 0 && (
                      <div className="flex flex-wrap gap-2.5" data-testid="step-facility">
                        {FACILITY_TYPES.map((t) => (
                          <Chip
                            key={t}
                            active={form.facility_type === t}
                            onClick={() => setForm((f) => ({ ...f, facility_type: t }))}
                          >
                            {t}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {step === 1 && (
                      <div className="flex flex-wrap gap-2.5" data-testid="step-sports">
                        {SPORT_OPTIONS.map((s) => (
                          <Chip
                            key={s}
                            active={form.sports.includes(s)}
                            onClick={() => toggle("sports", s)}
                          >
                            {s}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {step === 2 && (
                      <div className="flex flex-wrap gap-2.5" data-testid="step-products">
                        {PRODUCT_OPTIONS.map((p) => (
                          <Chip
                            key={p}
                            active={form.products.includes(p)}
                            onClick={() => toggle("products", p)}
                          >
                            {p}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {step === 3 && (
                      <div className="max-w-xl" data-testid="step-area">
                        <Field label="Approximate dimensions" required hint="For example 12 m × 10 m, or 1,800 sq ft">
                          <input
                            className={inputCls}
                            data-testid="project-area"
                            value={form.area}
                            onChange={set("area")}
                            placeholder="Room dimensions or total area"
                          />
                        </Field>
                      </div>
                    )}

                    {step === 4 && (
                      <div className="grid max-w-2xl gap-6 sm:grid-cols-2" data-testid="step-location">
                        <Field label="City" required>
                          <input
                            className={inputCls}
                            data-testid="project-city"
                            value={form.city}
                            onChange={set("city")}
                            placeholder="City"
                          />
                        </Field>
                        <Field label="State">
                          <input
                            className={inputCls}
                            data-testid="project-state"
                            value={form.state}
                            onChange={set("state")}
                            placeholder="State"
                          />
                        </Field>
                      </div>
                    )}

                    {step === 5 && (
                      <div className="flex flex-wrap gap-2.5" data-testid="step-timeline">
                        {TIMELINES.map((t) => (
                          <Chip
                            key={t}
                            active={form.timeline === t}
                            onClick={() => setForm((f) => ({ ...f, timeline: t }))}
                          >
                            {t}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {step === 6 && (
                      <div className="space-y-6" data-testid="step-contact">
                        <div className="grid gap-6 sm:grid-cols-2">
                          <Field label="Full name" required>
                            <input
                              className={inputCls}
                              data-testid="project-name"
                              value={form.full_name}
                              onChange={set("full_name")}
                              placeholder="Your name"
                            />
                          </Field>
                          <Field label="Gym or organisation">
                            <input
                              className={inputCls}
                              data-testid="project-org"
                              value={form.organisation}
                              onChange={set("organisation")}
                              placeholder="Facility name"
                            />
                          </Field>
                          <Field label="Phone" required>
                            <input
                              className={inputCls}
                              data-testid="project-phone"
                              value={form.phone}
                              onChange={set("phone")}
                              placeholder="+91 00000 00000"
                            />
                          </Field>
                          <Field label="Email" required>
                            <input
                              className={inputCls}
                              data-testid="project-email"
                              value={form.email}
                              onChange={set("email")}
                              placeholder="you@facility.com"
                            />
                          </Field>
                        </div>
                        <Field label="Anything else we should know?">
                          <textarea
                            className={`${inputCls} min-h-[120px]`}
                            data-testid="project-message"
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Columns, ceiling height, existing flooring, brand colours…"
                          />
                        </Field>
                        <Field label="Upload facility images or floor plan" hint="Up to 3 images, 2 MB each.">
                          <label className="inline-flex cursor-pointer items-center gap-3 border border-dashed border-[#111111]/25 px-5 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.16em] text-[#3B3B3B] transition-colors duration-300 hover:border-[#0F4C81] hover:text-[#0F4C81]">
                            <Upload className="h-4 w-4" />
                            {files.length ? `${files.length} file(s) selected` : "Choose files"}
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              data-testid="project-files"
                              onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 3))}
                            />
                          </label>
                        </Field>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-12 flex items-center justify-between border-t border-[#111111]/10 pt-8">
                <button
                  type="button"
                  data-testid="project-back"
                  disabled={step === 0}
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="inline-flex items-center gap-2 text-[0.6875rem] font-bold uppercase tracking-[0.18em] text-[#3B3B3B] transition-colors duration-300 hover:text-[#0F4C81] disabled:opacity-40"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>

                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    data-testid="project-next"
                    disabled={!canAdvance()}
                    onClick={() => setStep((s) => s + 1)}
                    className="inline-flex items-center gap-3 bg-[#0F4C81] px-8 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#1463A5] disabled:opacity-40"
                  >
                    Continue
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="project-submit"
                    disabled={busy}
                    onClick={submit}
                    className="inline-flex items-center gap-3 bg-[#111111] px-8 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#3B3B3B] disabled:opacity-60"
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {busy ? "Sending" : "Submit project"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
